import type {
  StageTemplateAdmin,
  CreateStageTemplateInput,
  UpdateStageTemplateInput,
  AdminStoryDto,
  AdminStoryViewerDto,
  AdminPollResultDto,
  CreateStoryInput,
  UpdateStoryInput,
  AdminAnnouncementDto,
  CreateAnnouncementInput,
  UpdateAnnouncementInput,
} from '@etap/shared';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const ADMIN_TOKEN_KEY = 'etap_admin_token';

export interface AdminStats {
  users: number;
  newUsersToday: number;
  projects: number;
  supportRequests: number;
}

export interface AdminStatsTimePoint {
  date: string;
  newUsers: number;
  activeUsers: number;
  visits: number;
  newVisitors: number;
}

export interface AdminStatsTimeseries {
  granularity: 'day' | 'month';
  points: AdminStatsTimePoint[];
}

export interface AdminPlannerPlans {
  plans: number;
  projects: number;
  creators: number;
  withWalls: number;
  withRooms: number;
  tried: number;
  triedUsers: number;
  roomsUsers: number;
  withOpenings: number;
  withFurniture: number;
  withElectrical: number;
  withPlumbing: number;
  withBackground: number;
  /** Планы с комнатами, где есть хоть один предмет любого слоя. */
  roomsWithItems: number;
}

export interface AdminPlannerAnalytics {
  range: { days: number; from: string };
  allTime: AdminPlannerPlans;
  period: AdminPlannerPlans;
  daily: { date: string; plans: number; users: number }[];
  funnel: {
    entered: number;
    created: number;
    opened: number;
    drew: number;
    roomFormed: number;
    usedLayers: number;
    returned: number;
  };
  landing: { clickers: number; signedUp: number; drew: number };
  templates: { template: string; count: number }[];
  autos: { layer: string; runs: number; users: number; avgItems: number | null; replaced: number }[];
  items: { type: string; layer: string; count: number }[];
  layers: { layer: string; switches: number; users: number }[];
  sessions: {
    sessions: number;
    edited: number;
    medianSeconds: number | null;
    avgWalls: number | null;
    avgUndos: number | null;
    readOnly: number;
  };
  entries: { target: string; fromLanding: boolean; count: number }[];
  seoPages: { page: string; visitors: number; clickers: number }[];
  people: {
    userId: string;
    name: string;
    username: string | null;
    plans: number;
    maxWalls: number;
    maxRooms: number;
    items: number;
    tried: boolean;
    fromLanding: boolean;
    lastEdit: string | null;
  }[];
}

export interface AdminAppAnalytics {
  range: { days: number; from: string };
  summary: {
    events: number;
    sessions: number;
    users: number;
    activeDays: number;
    eventsPerSession: number;
  };
  daily: { date: string; users: number; sessions: number; events: number }[];
  expenseFunnel: {
    opened: number;
    saved: number;
    abandoned: number;
    abandonRate: number | null;
    medianSaveSeconds: number | null;
  };
  abandonedByField: {
    field: string | null;
    count: number;
    users: number;
    medianSeconds: number | null;
  }[];
  fieldsUsed: { field: string; count: number; users: number }[];
  screens: { screen: string; views: number; users: number; medianSeconds: number | null }[];
  filters: { fields: string; count: number; users: number }[];
  budgetOverrun: {
    userId: string;
    username: string;
    seen: number;
    maxPct: number | null;
    lastSeenAt: string;
  }[];
  errors: { endpoint: string | null; status: string | null; count: number; users: number }[];
  events: { name: string; count: number; users: number }[];
}

export interface AdminUserActivity {
  user: { id: string; username: string | null; name: string };
  range: { days: number; from: string };
  summary: {
    events: number;
    sessions: number;
    activeDays: number;
    firstEventAt: string | null;
    lastEventAt: string | null;
  };
  daily: { date: string; events: number; sessions: number }[];
  expenseFunnel: { opened: number; saved: number; abandoned: number };
  fieldsUsed: { field: string; count: number }[];
  screens: { screen: string; views: number; medianSeconds: number | null }[];
  recent: {
    id: string;
    name: string;
    screen: string | null;
    props: Record<string, unknown> | null;
    sessionId: string;
    source: string;
    os: string | null;
    occurredAt: string;
  }[];
}

export interface AdminUser {
  id: string;
  userId: string;
  tgUserId: string | null;
  username: string | null;
  name: string;
  createdAt: string;
  lastSeenAt: string | null;
  projects: number;
  projectsTitles: string[];
  financeProjects: number;
  renovationProjects: number;
  supportRequests: number;
  activityToday: {
    expenses: number;
  };
  lastSeenSource: 'webapp' | 'bot' | 'unknown' | string;
  lastSeenOs: string | null;
  referralWuid: string | null;
  referrerDomain: string | null;
  utmSource: string | null;
  totalExpenses: number;
  /** Поведение из app_events за 30 дней; null — если событий не было. */
  behaviour30d: {
    sessions: number;
    events: number;
    formsOpened: number;
    formsAbandoned: number;
    lastEventAt: string | null;
  } | null;
  weeklyReportEnabled: boolean;
  weeklyReportDay: number;
  weeklyReportProjectId: string | null;
  recentActivity: {
    type: 'expense';
    date: string;
    description: string;
    amount?: string;
  }[];
  projectsDetail: {
    id: string;
    title: string;
    type: 'RENOVATION' | 'FINANCE';
    stages: {
      id: string;
      title: string;
      status: string;
      progressValue: number;
      progressType: string;
      itemsDone: number;
      itemsTotal: number;
    }[];
  }[];
}

export interface AdminUsersQuery {
  source?: 'all' | 'telegram' | 'webapp' | 'bot' | 'unknown';
  projects?: 'all' | 'with' | 'without';
  activity?: 'all' | 'today' | 'stale' | 'engaged' | 'ghost';
  report?: 'all' | 'on' | 'off';
  sort?: 'createdDesc' | 'lastSeenDesc' | 'activityDesc' | 'totalActivityDesc';
  page?: number;
  pageSize?: number;
}

export interface AdminUsersResponse {
  items: AdminUser[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminSupportRequest {
  id: string;
  message: string;
  contact: string | null;
  source: 'BOT' | 'WEBAPP';
  status: string;
  createdAt: string;
  user: {
    username: string | null;
    name: string;
  };
}

export interface AdminBroadcastBudgetRequest {
  mode: 'all' | 'test' | 'selected';
  targets?: string[];
}

export interface AdminBroadcastBudgetResult {
  mode: 'all' | 'test' | 'selected';
  attempted: number;
  sent: number;
  failed: number;
  recipientsTotal: number;
  resolvedTargets?: number;
  failedTargets?: string[];
  errors: Array<{
    userId: string;
    tgUserId: string;
    error: string;
  }>;
}

export interface AdminBroadcastSendRequest {
  mode: 'all' | 'test' | 'selected';
  targets?: string[];
  message: {
    text: string;
    buttonUrl?: string;
    parseMode?: 'HTML' | 'Markdown' | 'MarkdownV2';
  };
}

export interface AdminGuestAnalyticsSummary {
  visitorsTotal: number;
  visitors24h: number;
  eventsTotal: number;
  events24h: number;
  calculatorStateEvents: number;
  conversionsOpenApp: number;
  conversionsCta: number;
  conversionsRegistered: number;
  calculatorValues: {
    avgArea: number | null;
    avgBudget: number | null;
    avgRecommendedBudget: number | null;
    recommendedCount: number;
    customCount: number;
  };
}

export interface AdminGuestAnalyticsEvent {
  id: string;
  wuid: string;
  source: string;
  eventName: string;
  eventType: string;
  path: string | null;
  payload: unknown;
  occurredAt: string;
}

export interface AdminGuestAnalyticsVisitor {
  wuid: string;
  source: string;
  referrerDomain: string;
  utmSource: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  eventsCount: number;
  lastEventName: string | null;
  lastEventAt: string | null;
}

export interface AdminGuestAnalyticsResponse {
  summary: AdminGuestAnalyticsSummary;
  topEvents: Array<{ eventName: string; count: number }>;
  topCities: Array<{ city: string; count: number }>;
  topReferrers: Array<{ referrerDomain: string; visitors: number }>;
  recentEvents: AdminGuestAnalyticsEvent[];
  visitors: AdminGuestAnalyticsVisitor[];
}

export interface AdminWeeklyReportLog {
  id: string;
  userId: string;
  tgUserId: string | null;
  username: string | null;
  name: string;
  projectId: string | null;
  projectTitle: string | null;
  status: 'SENT' | 'FAILED' | 'SKIPPED';
  mode: 'AUTO' | 'MANUAL';
  errorMessage: string | null;
  messageBody: string | null;
  createdAt: string;
}

export interface AdminWeeklyReportsResponse {
  date: string;
  summary: { SENT: number; FAILED: number; SKIPPED: number };
  items: AdminWeeklyReportLog[];
}

export type { StageTemplateAdmin, CreateStageTemplateInput, UpdateStageTemplateInput };

export interface AdminExpenseCategory {
  id: number;
  name: string;
  slug: string;
  parentId: number | null;
  projectType: string | null;
  icon: string | null;
  color: string | null;
  order: number;
  isSystem: boolean;
  createdAt: string;
  children?: AdminExpenseCategory[];
}

export interface CreateExpenseCategoryInput {
  name: string;
  parentId?: number | null;
  projectType?: 'RENOVATION' | 'FINANCE' | null;
  icon?: string | null;
  color?: string | null;
  order?: number;
  isSystem?: boolean;
}

export type UpdateExpenseCategoryInput = Partial<CreateExpenseCategoryInput>;

const ADMIN_BASE = '/ops';

class AdminApiClient {
  private token: string | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<string> | null = null;

  constructor() {
    this.token = localStorage.getItem(ADMIN_TOKEN_KEY);
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem(ADMIN_TOKEN_KEY, token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem(ADMIN_TOKEN_KEY);
  }

  getToken() {
    return this.token;
  }

  private async refreshAccessToken(): Promise<string> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}${ADMIN_BASE}/refresh`, {
          method: 'POST',
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Admin refresh failed');
        }

        const data = await response.json();
        this.setToken(data.token);
        return data.token;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers as Record<string, string>,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    let res = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (res.status === 401 && !endpoint.includes('/refresh')) {
      try {
        const newToken = await this.refreshAccessToken();
        headers['Authorization'] = `Bearer ${newToken}`;

        res = await fetch(`${API_BASE_URL}${endpoint}`, {
          ...options,
          headers,
          credentials: 'include',
        });
      } catch {
        this.clearToken();
        throw new Error('Session expired. Please login again.');
      }
    }

    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.error || `HTTP ${res.status}`);
    }

    return res.json();
  }

  async login(username: string, password: string) {
    const response = await fetch(`${API_BASE_URL}${ADMIN_BASE}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const result = await response.json();
    this.setToken(result.token);
    return result;
  }

  async getStats(): Promise<AdminStats> {
    return this.request<AdminStats>(`${ADMIN_BASE}/stats`);
  }

  async getStatsTimeseries(params?: {
    granularity?: 'day' | 'month';
    days?: number;
    source?: 'all' | 'telegram' | 'webapp' | 'bot' | 'unknown';
  }): Promise<AdminStatsTimeseries> {
    const search = new URLSearchParams();
    if (params?.granularity) search.set('granularity', params.granularity);
    if (params?.days) search.set('days', String(params.days));
    if (params?.source) search.set('source', params.source);
    const suffix = search.toString() ? `?${search.toString()}` : '';
    return this.request<AdminStatsTimeseries>(`${ADMIN_BASE}/stats/timeseries${suffix}`);
  }

  async getUsers(params?: AdminUsersQuery): Promise<AdminUsersResponse> {
    const search = new URLSearchParams();
    if (params?.source) search.set('source', params.source);
    if (params?.projects) search.set('projects', params.projects);
    if (params?.activity) search.set('activity', params.activity);
    if (params?.report) search.set('report', params.report);
    if (params?.sort) search.set('sort', params.sort);
    if (params?.page) search.set('page', String(params.page));
    if (params?.pageSize) search.set('pageSize', String(params.pageSize));
    const suffix = search.toString() ? `?${search.toString()}` : '';
    return this.request<AdminUsersResponse>(`${ADMIN_BASE}/users${suffix}`);
  }

  async getWeeklyReports(params?: {
    date?: string;
    status?: 'SENT' | 'FAILED' | 'SKIPPED';
    mode?: 'AUTO' | 'MANUAL';
    take?: number;
  }): Promise<AdminWeeklyReportsResponse> {
    const search = new URLSearchParams();
    if (params?.date) search.set('date', params.date);
    if (params?.status) search.set('status', params.status);
    if (params?.mode) search.set('mode', params.mode);
    if (params?.take) search.set('take', String(params.take));
    const suffix = search.toString() ? `?${search.toString()}` : '';
    return this.request<AdminWeeklyReportsResponse>(`${ADMIN_BASE}/weekly-reports${suffix}`);
  }

  async updateUser(
    id: string,
    data: { weeklyReportEnabled?: boolean; weeklyReportDay?: number },
  ): Promise<{ id: string; weeklyReportEnabled: boolean; weeklyReportDay: number }> {
    return this.request<{ id: string; weeklyReportEnabled: boolean; weeklyReportDay: number }>(
      `${ADMIN_BASE}/users/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    );
  }

  async sendUserReport(id: string): Promise<{ success: boolean; projectCount: number }> {
    return this.request<{ success: boolean; projectCount: number }>(
      `${ADMIN_BASE}/users/${id}/send-report`,
      { method: 'POST' },
    );
  }

  async getSupport(): Promise<AdminSupportRequest[]> {
    return this.request<AdminSupportRequest[]>(`${ADMIN_BASE}/support`);
  }

  async getGuestAnalytics(params?: {
    takeEvents?: number;
    takeVisitors?: number;
    eventName?: string;
    wuid?: string;
  }): Promise<AdminGuestAnalyticsResponse> {
    const search = new URLSearchParams();
    if (params?.takeEvents) search.set('takeEvents', String(params.takeEvents));
    if (params?.takeVisitors) search.set('takeVisitors', String(params.takeVisitors));
    if (params?.eventName) search.set('eventName', params.eventName);
    if (params?.wuid) search.set('wuid', params.wuid);
    const suffix = search.toString() ? `?${search.toString()}` : '';
    return this.request<AdminGuestAnalyticsResponse>(`${ADMIN_BASE}/guest-analytics${suffix}`);
  }

  async getAppAnalytics(params?: { days?: number }): Promise<AdminAppAnalytics> {
    const query = new URLSearchParams();
    if (params?.days) query.set('days', String(params.days));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request<AdminAppAnalytics>(`${ADMIN_BASE}/app-analytics${suffix}`);
  }

  async getPlannerAnalytics(params?: { days?: number }): Promise<AdminPlannerAnalytics> {
    const query = new URLSearchParams();
    if (params?.days) query.set('days', String(params.days));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request<AdminPlannerAnalytics>(`${ADMIN_BASE}/planner-analytics${suffix}`);
  }

  async getUserActivity(id: string, params?: { days?: number }): Promise<AdminUserActivity> {
    const query = new URLSearchParams();
    if (params?.days) query.set('days', String(params.days));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request<AdminUserActivity>(`${ADMIN_BASE}/users/${id}/activity${suffix}`);
  }

  async sendBudgetCalculatorBroadcast(
    data: AdminBroadcastBudgetRequest
  ): Promise<AdminBroadcastBudgetResult> {
    return this.request<AdminBroadcastBudgetResult>(`${ADMIN_BASE}/broadcast/budget-calculator`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async sendBroadcast(
    data: AdminBroadcastSendRequest
  ): Promise<AdminBroadcastBudgetResult> {
    return this.request<AdminBroadcastBudgetResult>(`${ADMIN_BASE}/broadcasts/send`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTemplates(): Promise<StageTemplateAdmin[]> {
    return this.request<StageTemplateAdmin[]>(`${ADMIN_BASE}/templates`);
  }

  async getTemplate(id: string): Promise<StageTemplateAdmin> {
    return this.request<StageTemplateAdmin>(`${ADMIN_BASE}/templates/${id}`);
  }

  async createTemplate(data: CreateStageTemplateInput): Promise<StageTemplateAdmin> {
    return this.request<StageTemplateAdmin>(`${ADMIN_BASE}/templates`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTemplate(id: string, data: UpdateStageTemplateInput): Promise<StageTemplateAdmin> {
    return this.request<StageTemplateAdmin>(`${ADMIN_BASE}/templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteTemplate(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`${ADMIN_BASE}/templates/${id}`, {
      method: 'DELETE',
    });
  }

  async duplicateTemplate(id: string): Promise<StageTemplateAdmin> {
    return this.request<StageTemplateAdmin>(`${ADMIN_BASE}/templates/${id}/duplicate`, {
      method: 'POST',
    });
  }

  async clearAiCache(): Promise<{ cleared: number }> {
    return this.request<{ cleared: number }>(`${ADMIN_BASE}/ai-cache`, {
      method: 'DELETE',
    });
  }

  async getExpenseCategories(): Promise<AdminExpenseCategory[]> {
    return this.request<AdminExpenseCategory[]>(`${ADMIN_BASE}/expense-categories`);
  }

  async createExpenseCategory(data: CreateExpenseCategoryInput): Promise<AdminExpenseCategory> {
    return this.request<AdminExpenseCategory>(`${ADMIN_BASE}/expense-categories`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateExpenseCategory(id: number, data: UpdateExpenseCategoryInput): Promise<AdminExpenseCategory> {
    return this.request<AdminExpenseCategory>(`${ADMIN_BASE}/expense-categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteExpenseCategory(id: number): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`${ADMIN_BASE}/expense-categories/${id}`, {
      method: 'DELETE',
    });
  }

  // Stories
  // ─── Анонсы ────────────────────────────────────────────────────────────────

  async getAnnouncements(): Promise<AdminAnnouncementDto[]> {
    return this.request<AdminAnnouncementDto[]>(`${ADMIN_BASE}/announcements`);
  }

  async createAnnouncement(data: CreateAnnouncementInput): Promise<AdminAnnouncementDto> {
    return this.request<AdminAnnouncementDto>(`${ADMIN_BASE}/announcements`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAnnouncement(id: string, data: UpdateAnnouncementInput): Promise<AdminAnnouncementDto> {
    return this.request<AdminAnnouncementDto>(`${ADMIN_BASE}/announcements/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAnnouncement(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`${ADMIN_BASE}/announcements/${id}`, {
      method: 'DELETE',
    });
  }

  async getStories(): Promise<AdminStoryDto[]> {
    return this.request<AdminStoryDto[]>(`${ADMIN_BASE}/stories`);
  }

  async createStory(data: CreateStoryInput): Promise<AdminStoryDto> {
    return this.request<AdminStoryDto>(`${ADMIN_BASE}/stories`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStory(id: string, data: UpdateStoryInput): Promise<AdminStoryDto> {
    return this.request<AdminStoryDto>(`${ADMIN_BASE}/stories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteStory(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`${ADMIN_BASE}/stories/${id}`, {
      method: 'DELETE',
    });
  }

  async getStoryViews(key: string): Promise<AdminStoryViewerDto[]> {
    return this.request<AdminStoryViewerDto[]>(`${ADMIN_BASE}/stories/${encodeURIComponent(key)}/views`);
  }

  async getPollResults(key: string): Promise<AdminPollResultDto[]> {
    return this.request<AdminPollResultDto[]>(`${ADMIN_BASE}/stories/${encodeURIComponent(key)}/poll-results`);
  }
}

export const adminApi = new AdminApiClient();
